import { FC } from 'react';
import { useIntl } from 'react-intl';
import { isEqual } from 'lodash';
import { useSessionStorage } from 'usehooks-ts';
import { faBook, faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons';

import { RecordsWithTimestamp } from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useFormattedSearches,
  useRecentActivityInstancesFetch,
  useRecentInstancesRemoval,
  useRecentSearchesFetch,
  useRecentSearchesRemoval,
  useRemoveActivityMutationEffect,
  UserSearchDto,
  useSavedSearchesFetch,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { Route, storageKeys } from '@app/constants';

import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { DashboardSearchModalProvider } from '../DashboardSearchModal';
import { useBatchInstancesFetch } from '../hooks/useBatchInstancesFetch';

export const RecentActivityPage: FC = () => {
  const { isLoggedIn } = useAppContext();
  const { $t } = useIntl();
  const [searchHistory, setSearchHistory] = useSessionStorage<UserSearchDto[]>(
    storageKeys.searchHistory,
    []
  );
  const formatSearches = useFormattedSearches();
  const instancesQuery = useRecentActivityInstancesFetch();
  const searchesQuery = useRecentSearchesFetch();
  const savedSearchesQuery = useSavedSearchesFetch();
  const [viewedRecords, setViewedRecords] = useSessionStorage<
    RecordsWithTimestamp[]
  >(storageKeys.viewedRecords, []);
  const [savedSearches] = useSessionStorage<UserSearchDto[]>(
    storageKeys.savedSearches,
    []
  );
  const viewedRecordsDataQuery = useBatchInstancesFetch({
    queryKey: 'viewedRecords',
    list: isLoggedIn ? instancesQuery.data : viewedRecords,
  });
  const removeSearchMutation = useRecentSearchesRemoval();
  const removeInstancesMutation = useRecentInstancesRemoval();

  useRemoveActivityMutationEffect(removeSearchMutation, searchesQuery);
  useRemoveActivityMutationEffect(removeInstancesMutation, instancesQuery);

  const handleSearchEntryRemoval = entryIndex => {
    if (isLoggedIn) {
      removeSearchMutation.mutate([entryIndex]);
    } else {
      setSearchHistory(prevValue =>
        prevValue.filter((searches, index) => index !== entryIndex)
      );
    }
  };

  const handleViewedRecordRemoval = ({
    instanceId,
    timestamp,
    id,
  }: RecordsWithTimestamp) => {
    if (isLoggedIn && id) {
      removeInstancesMutation.mutate([id]);
    } else {
      setViewedRecords(prevValue =>
        prevValue.filter(
          viewedRecord => !isEqual(viewedRecord, { instanceId, timestamp })
        )
      );
    }
  };

  return (
    <DashboardSearchModalProvider>
      <HistoryRecordsPageWrapper
        pageTitle={$t(sharedMessages.recentActivity)}
        parentRoute={Route.recentActivity}
        searchesTabConfig={{
          title: $t(sharedMessages.searchHistory),
          documentTitle: $t({
            id: 'recentActivity.searchHistoryDocumentTitle',
            defaultMessage: 'Search history - Recent activity - EBSCO Locate',
          }),
          route: Route.recentSearchHistory,
          list: isLoggedIn ? searchesQuery.data : formatSearches(searchHistory),
          isError: searchesQuery.isError,
          isLoading:
            searchesQuery.isFetching || removeSearchMutation.status !== 'idle',
          refetch: searchesQuery.refetch,
          onEntryRemoval: handleSearchEntryRemoval,
          emptyStateMessage: $t({
            id: 'recentActivity.emptyStateSearchMessage',
            defaultMessage: 'You currently have no recent searches.',
          }),
          emptyStateIcon: faMagnifyingGlass,
          savedSearchesList: isLoggedIn
            ? savedSearchesQuery.data
            : formatSearches(savedSearches),
        }}
        recordsTabConfig={{
          title: $t({
            id: 'recentActivity.viewedRecords',
            defaultMessage: 'Viewed records',
          }),
          documentTitle: $t({
            id: 'recentActivity.viewedRecordsDocumentTitle',
            defaultMessage: 'Viewed records - Recent activity - EBSCO Locate',
          }),
          route: Route.recentViewedRecords,
          list: viewedRecordsDataQuery.data,
          isError: instancesQuery.isError || viewedRecordsDataQuery.isError,
          isLoading:
            instancesQuery.isFetching ||
            viewedRecordsDataQuery.isFetching ||
            removeInstancesMutation.status !== 'idle',
          refetch: () =>
            instancesQuery.isError
              ? instancesQuery.refetch()
              : viewedRecordsDataQuery.refetch(),
          onEntryRemoval: handleViewedRecordRemoval,
          emptyStateMessage: $t({
            id: 'recentActivity.emptyStateRecordMessage',
            defaultMessage: 'You currently have no recently viewed records.',
          }),
          emptyStateIcon: faBook,
        }}
      />
    </DashboardSearchModalProvider>
  );
};
